'use client';

import dynamic from 'next/dynamic';
import { Copytext } from '@/app/_components/core/copytext';
import { Headline } from '@/app/_components/core/headline';
import { RichText } from '@/app/_components/core/rich-text';
import { TwoColumnLayout } from '@/app/_components/core/two-column-layout';
import { cn } from '@/app/_lib/utils';
import { MediaBlock } from './components';
import type { FormEmbedComponentProps, EventMeta } from './form-embed.types';

// Custom form handler - loaded from @custom/_exports registry
// Allows project-specific form providers (HubSpot, etc.)
const FormEmbedFormHandler = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.FormEmbedFormHandler,
    })),
  { ssr: false }
);

// Form embed additional content - loaded from @custom/_exports registry
const FormEmbedAdditionalContent = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.FormEmbedAdditionalContent,
    })),
  { ssr: true }
);

const THEME_CLASS_MAP: Record<string, string> = {
  theme0: 'bg-background',
  theme1: 'bg-form-embed-accent',
};

type FormEmbed2ColProps = Pick<FormEmbedComponentProps, 'component'> & {
  eventMeta?: EventMeta;
  priorityMedia?: boolean;
};

export function FormEmbed2Col({ component, eventMeta, priorityMedia }: FormEmbed2ColProps) {
  const {
    bodytext,
    embedIntro,
    html,
    image,
    video,
    theme,
    mediaType,
    mediaPosition,
  } = component;

  const themeClass = THEME_CLASS_MAP[theme] ?? THEME_CLASS_MAP.theme0;
  // Show media based on mediaType: 'image' shows image, 'video' shows video, 'none' shows nothing
  const showImage = mediaType === 'image' && image;
  const showVideo = mediaType === 'video' && video && video.length > 0;
  const hasMedia = showImage || showVideo;
  // Text is light on media/dark backgrounds, dark on light backgrounds
  const textColorClass =
    hasMedia || theme === 'theme1'
      ? 'text-primary-foreground'
      : 'text-foreground';

  return (
    <TwoColumnLayout position={mediaPosition}>
      <TwoColumnLayout.Left
        className={cn(
          'grid aspect-4/5 lg:rounded-form-embed-media',
          themeClass
        )}
      >
        {hasMedia && (
          <div className="col-start-1 row-start-1">
            <MediaBlock
              image={showImage ? image : undefined}
              video={showVideo ? video : undefined}
              priority={priorityMedia}
            />
          </div>
        )}

        <div className="z-10 col-start-1 row-start-1 flex flex-col justify-between px-6 py-section-padding-y-mobile md:px-16 xl:py-20">
          <FormEmbedAdditionalContent />

          {bodytext && (
            <Copytext
              className={cn('mb-0 whitespace-pre-line', textColorClass)}
            >
              {bodytext}
            </Copytext>
          )}
        </div>
      </TwoColumnLayout.Left>

      <TwoColumnLayout.Right className="bg-background px-container-padding-x-mobile text-center lg:flex lg:flex-col lg:items-center lg:justify-center xl:px-container-padding-x-desktop">
        <div className="w-full">
          {embedIntro && (
            <RichText className="mb-8 lg:-ml-16 lg:*:mx-16">
              {embedIntro}
            </RichText>
          )}
          {eventMeta && (
            <div className="mb-8 text-center">
              <Headline as="h4">{eventMeta.title}</Headline>
              <Copytext className="mb-0">
                <strong>{eventMeta.dateTimeLabel}:</strong> {eventMeta.dateTime}
                {eventMeta.location && (
                  <>
                    <br />
                    <strong>{eventMeta.locationLabel}:</strong>{' '}
                    {eventMeta.location}
                  </>
                )}
              </Copytext>
            </div>
          )}
          <FormEmbedFormHandler html={html} variant="2col" />
        </div>
      </TwoColumnLayout.Right>
    </TwoColumnLayout>
  );
}
